import * as React from 'react';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    Collapse,
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import PrivateClubMatchSummary from '../components/PrivateClubMatchSummary';
import {
    createPrivateClubTemplate,
    cancelPrivateClubRound,
    deletePrivateClubTemplate,
    getPrivateClubAgenticConfiguration,
    getPrivateClubAgenticEvents,
    getPrivateClubAgenticLogs,
    getPrivateClubAgenticRuns,
    getPrivateClubBetTypeGroups,
    getLeagueById,
    getLeagueScheduledMatches,
    getPaginatedSports,
    getPrivateClubLeagueCandidates,
    getPrivateClubMatchBetGroups,
    getPrivateClubMatchDetails,
    getPrivateClubScheduledMatches,
    getPrivateClubTemplates,
    markAllPrivateClubAgenticEventsRead,
    markPrivateClubAgenticEventRead,
    runPrivateClubAgenticTemplateCreation,
    runPrivateClubTemplateAgent,
    updatePrivateClubTemplate,
    updatePrivateClubAgenticConfiguration,
} from '../Api';
import {
    PrivateClubBetGroupAvailabilityDto,
    PrivateClubRoundStatus,
    PrivateClubTemplateStatus,
} from '../models/dtos/PrivateClubDto';
import {
    AgentRunAdminDto,
    PrivateClubAgenticConfigurationAdminDto,
    PrivateClubAgenticEventAdminDto,
    PrivateClubAgenticLogAdminDto,
    PrivateClubBetTypeGroupOptionAdminDto,
    PrivateClubTemplateAdminDto,
    UpsertPrivateClubTemplateAdminDto,
} from '../models/dtos/PrivateClubAdminDto';
import LeagueInfoDto, { SportInfoDto } from '../models/dtos/LeagueInfoDto';
import MatchInfoDto from '../models/dtos/MatchInfoDto';
import SportEntityDto from '../models/dtos/SportEntityDto';

type LeagueScheduleState = {
    loading: boolean;
    count: number | null;
    error: boolean;
};

type SportOption = {
    id: number;
    name: string;
    photoUrl: string | null;
};

type PromoMetadataRow = {
    uiId: string;
    key: string;
    value: string;
};

type MatchForm = {
    uiId: string;
    matchId: string;
    allowedBetTypeGroupIds: number[];
    settlementStatus: 'PENDING' | 'EXCLUDED' | 'SETTLED';
};

type RoundForm = {
    uiId: string;
    id?: string | null;
    index: string;
    startsAt: string;
    endsAt: string;
    status: PrivateClubRoundStatus;
    hasPicks: boolean;
    matches: MatchForm[];
};

type TemplateForm = {
    title: string;
    sportFilterId: string;
    sportFilterLabel: string;
    sportFilterPhotoUrl: string;
    sportLeagueId: string;
    sportLeagueLabel: string;
    sportLeaguePhotoUrl: string;
    scorePointMultiplier: string;
    maxPicksPerMatch: string;
    allowedBetTypeGroupIds: number[];
    agentInstructions: string;
    agenticMode: boolean;
    status: PrivateClubTemplateStatus;
    willSettleAt: string;
    promoMetadataRows: PromoMetadataRow[];
    rounds: RoundForm[];
};

/** The private club agent's AI in the AI hub (betup-server-ai), edited in AI › Agents / Tasks. */
const ROUND_PLANNER_AGENT_CODE = 'private-club.round-planner';
const SELECT_MATCHES_TASK_CODE = 'private-club.select-matches';

type AgenticSettingsForm = {
    automaticRoundCreationEnabled: boolean;
    scheduleRunIntervalMinutes: string;
    lowRunwayHours: string;
    defaultOpenLeadHours: string;
    defaultRoundMatchLimit: string;
    nonAgenticWarningRunwayHours: string;
    templateAutoGenerationEnabled: boolean;
    templateAutoGenerationAllowedSports: SportOption[];
    templateAutoGenerationAllowedBetTypeGroupIds: number[];
    templateAutoGenerationRules: string;
    templateCreationLookaheadHours: string;
    templateCreationMinMatches: string;
    maxActiveTemplates: string;
    maxTemplatesCreatedPerRun: string;
};

type AgentConfirmation =
    | { action: 'GENERATE_TEMPLATES' }
    | { action: 'GENERATE_ROUNDS'; template: PrivateClubTemplateAdminDto };

const maxPicksPerMatchOptions = [1, 2, 3, 4] as const;
const defaultAllowedBetTypeGroupIds = [1, 5, 6, 7, 46, 47];

function uiId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const emptyForm = (): TemplateForm => ({
    title: '',
    sportFilterId: '',
    sportFilterLabel: '',
    sportFilterPhotoUrl: '',
    sportLeagueId: '',
    sportLeagueLabel: '',
    sportLeaguePhotoUrl: '',
    scorePointMultiplier: '10',
    maxPicksPerMatch: '2',
    allowedBetTypeGroupIds: defaultAllowedBetTypeGroupIds,
    agentInstructions: '',
    agenticMode: false,
    status: 'INACTIVE',
    willSettleAt: '',
    promoMetadataRows: [],
    rounds: [],
});

function emptyMatch(): MatchForm {
    return {
        uiId: uiId(),
        matchId: '',
        allowedBetTypeGroupIds: [],
        settlementStatus: 'PENDING',
    };
}

function emptyRound(index: number): RoundForm {
    return {
        uiId: uiId(),
        index: String(index),
        startsAt: '',
        endsAt: '',
        status: 'DRAFT',
        hasPicks: false,
        matches: [],
    };
}

function prefilledRound(rounds: RoundForm[]): RoundForm {
    const now = new Date();
    const previousEnd = rounds
        .map(round => parseDateTimeLocal(round.endsAt))
        .filter((date): date is Date => date != null)
        .sort((a, b) => b.getTime() - a.getTime())[0];
    const startsAt = previousEnd && previousEnd.getTime() > now.getTime()
        ? previousEnd
        : nextLocalMidnight(now);
    return {
        ...emptyRound((rounds.length > 0 ? Math.max(...rounds.map(round => Number(round.index) || 0)) : 0) + 1),
        startsAt: toDateTimeLocalFromDate(startsAt),
        endsAt: toDateTimeLocalFromDate(addDays(startsAt, 7)),
    };
}

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function agenticSettingsToForm(
    config: PrivateClubAgenticConfigurationAdminDto,
    sports: SportOption[],
): AgenticSettingsForm {
    return {
        automaticRoundCreationEnabled: config.automaticRoundCreationEnabled ?? true,
        scheduleRunIntervalMinutes: String(config.scheduleRunIntervalMinutes),
        lowRunwayHours: String(config.lowRunwayHours),
        defaultOpenLeadHours: String(config.defaultOpenLeadHours),
        defaultRoundMatchLimit: String(config.defaultRoundMatchLimit),
        nonAgenticWarningRunwayHours: String(config.nonAgenticWarningRunwayHours),
        templateAutoGenerationEnabled: config.templateAutoGenerationEnabled,
        templateAutoGenerationAllowedSports: config.templateAutoGenerationAllowedSportIds.map(sportId => (
            sports.find(sport => sport.id === sportId) ?? {
                id: sportId,
                name: `Sport ${sportId}`,
                photoUrl: null,
            }
        )),
        templateAutoGenerationAllowedBetTypeGroupIds: (config.templateAutoGenerationAllowedBetTypeGroupIds ?? defaultAllowedBetTypeGroupIds)
            .filter(item => Number.isFinite(item) && item > 0),
        templateAutoGenerationRules: config.templateAutoGenerationRules,
        templateCreationLookaheadHours: String(config.templateCreationLookaheadHours),
        templateCreationMinMatches: String(config.templateCreationMinMatches),
        maxActiveTemplates: String(config.maxActiveTemplates),
        maxTemplatesCreatedPerRun: String(config.maxTemplatesCreatedPerRun),
    };
}

function parseNumberField(value: string, field: string) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) throw new Error(`${field} must be a number.`);
    return parsed;
}

function agenticSettingsPayload(form: AgenticSettingsForm) {
    const allowedBetTypeGroupIds = Array.from(new Set(form.templateAutoGenerationAllowedBetTypeGroupIds))
        .filter(item => Number.isFinite(item) && item > 0)
        .sort((a, b) => a - b);
    if (allowedBetTypeGroupIds.length === 0) {
        throw new Error('At least one allowed bet group must be selected for agentic template creation.');
    }
    return {
        automaticRoundCreationEnabled: form.automaticRoundCreationEnabled,
        scheduleRunIntervalMinutes: parseNumberField(form.scheduleRunIntervalMinutes, 'Schedule interval'),
        lowRunwayHours: parseNumberField(form.lowRunwayHours, 'Low runway hours'),
        defaultOpenLeadHours: parseNumberField(form.defaultOpenLeadHours, 'Open lead hours'),
        defaultRoundMatchLimit: parseNumberField(form.defaultRoundMatchLimit, 'Default round match limit'),
        nonAgenticWarningRunwayHours: parseNumberField(form.nonAgenticWarningRunwayHours, 'Non-agentic warning runway'),
        templateAutoGenerationEnabled: form.templateAutoGenerationEnabled,
        templateAutoGenerationAllowedSportIds: form.templateAutoGenerationAllowedSports
            .map(sport => sport.id)
            .filter(item => Number.isFinite(item) && item > 0),
        templateAutoGenerationAllowedBetTypeGroupIds: allowedBetTypeGroupIds,
        templateAutoGenerationRules: form.templateAutoGenerationRules.trim(),
        templateCreationLookaheadHours: parseNumberField(form.templateCreationLookaheadHours, 'Template creation lookahead'),
        templateCreationMinMatches: parseNumberField(form.templateCreationMinMatches, 'Template creation min matches'),
        maxActiveTemplates: parseNumberField(form.maxActiveTemplates, 'Max active templates'),
        maxTemplatesCreatedPerRun: parseNumberField(form.maxTemplatesCreatedPerRun, 'Max templates per run'),
    };
}

function formatDetails(details: Record<string, unknown>) {
    const keys = Object.keys(details ?? {});
    if (keys.length === 0) return '';
    try {
        return JSON.stringify(details, null, 2);
    } catch {
        return '';
    }
}

function agenticSettingLabel(label: string, description: string) {
    return (
        <Stack direction="row" component="span" alignItems="center" gap={0.5}>
            <span>{label}</span>
            <Tooltip title={description} arrow>
                <InfoOutlinedIcon
                    color="action"
                    fontSize="inherit"
                    sx={{ cursor: 'help', verticalAlign: 'middle' }}
                />
            </Tooltip>
        </Stack>
    );
}

function statusColor(status: PrivateClubTemplateStatus): 'success' | 'warning' | 'error' | 'default' {
    if (status === 'ACTIVE') return 'success';
    if (status === 'INACTIVE') return 'warning';
    if (status === 'CANCELLED') return 'error';
    return 'default';
}

function roundStatusColor(status: PrivateClubRoundStatus): 'success' | 'info' | 'warning' | 'error' | 'default' {
    if (status === 'OPEN') return 'success';
    if (status === 'SCHEDULED') return 'info';
    if (status === 'PROCESSING') return 'warning';
    if (status === 'CANCELLED') return 'error';
    return 'default';
}

function derivedRoundStatus(round: RoundForm, now: Date = new Date()): PrivateClubRoundStatus {
    if (round.status === 'CANCELLED') return 'CANCELLED';
    if (round.status === 'SETTLED') return 'SETTLED';
    const startsAt = parseDateTimeLocal(round.startsAt);
    const endsAt = parseDateTimeLocal(round.endsAt);
    if (round.matches.length === 0 || startsAt == null || endsAt == null) return 'DRAFT';
    if (now.getTime() >= endsAt.getTime()) return 'PROCESSING';
    if (now.getTime() < startsAt.getTime()) return 'SCHEDULED';
    return 'OPEN';
}

function toDateTimeLocal(value?: string | null) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

function toDateTimeLocalFromDate(date: Date) {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

function nextLocalMidnight(reference: Date = new Date()) {
    return new Date(reference.getFullYear(), reference.getMonth(), reference.getDate() + 1, 0, 0, 0, 0);
}

function addDays(date: Date, days: number) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function parseDateTimeLocal(value: string) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function fromDateTimeLocal(value: string, field: string) {
    if (!value.trim()) throw new Error(`${field} is required.`);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) throw new Error(`${field} is invalid.`);
    return date.toISOString();
}

function leagueLabel(league: LeagueInfoDto) {
    return `[${league.id}] ${league.name ?? `League ${league.id}`}${league.sport ? ' - ' + league.sport.name : ''}`;
}

function sportLabel(sport: SportOption | SportInfoDto) {
    return sport.name ?? `Sport ${sport.id}`;
}

function leaguePhotoUrl(league: LeagueInfoDto) {
    return league.photoUrl ?? league.photo_url ?? null;
}

function sportPhotoUrl(sport: SportInfoDto) {
    return sport.photoUrl ?? sport.photo_url ?? null;
}

function validateTemplateStatusForSave(form: TemplateForm) {
    const playableStatuses: PrivateClubRoundStatus[] = ['SCHEDULED', 'OPEN'];
    const now = new Date();
    const willSettleAt = parseDateTimeLocal(form.willSettleAt);
    if (form.willSettleAt.trim() && willSettleAt == null) {
        throw new Error('Will settle at is invalid.');
    }
    if (willSettleAt != null) {
        const latestPlayableRoundEnd = form.rounds
            .filter(round => playableStatuses.includes(derivedRoundStatus(round, now)))
            .map(round => parseDateTimeLocal(round.endsAt))
            .filter((date): date is Date => date != null)
            .sort((a, b) => b.getTime() - a.getTime())[0];
        if (latestPlayableRoundEnd && willSettleAt.getTime() <= latestPlayableRoundEnd.getTime()) {
            throw new Error('Will settle at must be after the end time of every scheduled or open round.');
        }
    }

    if (form.status === 'ACTIVE') {
        const hasPlayableRound = form.rounds.some(round => {
            const endsAt = parseDateTimeLocal(round.endsAt);
            return playableStatuses.includes(derivedRoundStatus(round, now))
                && endsAt != null
                && endsAt.getTime() > now.getTime()
                && round.matches.some(match => match.settlementStatus !== 'EXCLUDED');
        });
        if (!hasPlayableRound) {
            throw new Error('Active template requires at least one scheduled or open round with at least one match.');
        }
    }

    if (form.status === 'SETTLED') {
        const unsettledRoundLabels = form.rounds
            .filter(round => derivedRoundStatus(round, now) !== 'SETTLED')
            .map(round => round.index || '-');
        if (form.rounds.length === 0 || unsettledRoundLabels.length > 0) {
            throw new Error(`Settled template requires every round to be settled: ${unsettledRoundLabels.join(', ') || 'no rounds'}.`);
        }
    }
}

function sportEntityToOption(sport: SportEntityDto): SportOption {
    return {
        id: sport.id,
        name: sport.name,
        photoUrl: sport.photo_url ?? null,
    };
}

function findDefaultSport(sports: SportOption[]) {
    return sports.find(sport => sport.name?.trim().toLowerCase() === 'football') ?? null;
}

function withSelectedSport(form: TemplateForm, sport: SportOption): TemplateForm {
    return {
        ...form,
        sportFilterId: String(sport.id),
        sportFilterLabel: sportLabel(sport),
        sportFilterPhotoUrl: sport.photoUrl ?? '',
    };
}

function RoundMatchDetails({ matchId, match }: { matchId: number; match: MatchInfoDto | null | undefined }) {
    return <PrivateClubMatchSummary matchId={matchId} match={match} showStats />;
}

function RoundMatchDetailsForId(props: { matchId: string; matchDetailsById: Record<number, MatchInfoDto | null> }) {
    const matchId = Number(props.matchId);
    if (!Number.isFinite(matchId) || matchId <= 0) return null;
    const hasDetails = Object.prototype.hasOwnProperty.call(props.matchDetailsById, matchId);
    return <RoundMatchDetails matchId={matchId} match={hasDetails ? props.matchDetailsById[matchId] : undefined} />;
}

function betTypeGroupOptionLabel(option: PrivateClubBetTypeGroupOptionAdminDto) {
    return `${option.name || 'Bet group'} (#${option.id})`;
}

function selectedBetTypeGroupOption(
    groupId: number,
    options: PrivateClubBetTypeGroupOptionAdminDto[],
): PrivateClubBetTypeGroupOptionAdminDto {
    return options.find(option => option.id === groupId) ?? { id: groupId, name: `Bet group ${groupId}` };
}

function TemplateBetGroupsSelect(props: {
    value: number[];
    options: PrivateClubBetTypeGroupOptionAdminDto[];
    loading: boolean;
    disabled?: boolean;
    onChange: (value: number[]) => void;
}) {
    const selected = props.value.map(groupId => selectedBetTypeGroupOption(groupId, props.options));
    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={props.options}
            value={selected}
            loading={props.loading}
            disabled={props.disabled}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={betTypeGroupOptionLabel}
            onChange={(_, value) => props.onChange(Array.from(new Set(value.map(option => option.id))).sort((a, b) => a - b))}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        size="small"
                        label={betTypeGroupOptionLabel(option)}
                    />
                ))
            }
            renderOption={(optionProps, option, { selected }) => (
                <Box component="li" {...optionProps}>
                    <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                    <Typography variant="body2">{betTypeGroupOptionLabel(option)}</Typography>
                </Box>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Allowed bet groups"
                    placeholder="Search bet groups"
                    helperText="Applies to all rounds and matches in this template."
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {props.loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

function RoundMatchBetGroups(props: {
    matchId: string;
    templateGroupIds: number[];
    groupsByMatchId: Record<number, PrivateClubBetGroupAvailabilityDto[] | null>;
}) {
    const matchId = Number(props.matchId);
    if (!Number.isFinite(matchId) || matchId <= 0) return null;
    const hasGroups = Object.prototype.hasOwnProperty.call(props.groupsByMatchId, matchId);
    const groups = hasGroups ? props.groupsByMatchId[matchId] : undefined;
    if (groups === undefined) {
        return <Typography variant="body2" color="text.secondary">Loading bet groups...</Typography>;
    }
    if (groups === null) {
        return <Typography variant="body2" color="error">Bet groups unavailable</Typography>;
    }
    if (groups.length === 0) {
        return <Typography variant="body2" color="text.secondary">No supported bet groups</Typography>;
    }

    return (
        <Stack direction="row" gap={1} flexWrap="wrap">
            {groups
                .filter(group => props.templateGroupIds.includes(group.betTypeGroupId))
                .map(group => (
                    <Chip
                        key={group.betTypeGroupId}
                        size="small"
                        color={group.availableBetsCount > 0 ? 'success' : 'default'}
                        label={`${group.label} (#${group.betTypeGroupId}) - ${group.availableBetsCount}`}
                    />
                ))}
        </Stack>
    );
}

function leagueSportToOption(sport: SportInfoDto | null): SportOption | null {
    if (!sport) return null;
    return {
        id: sport.id,
        name: sport.name,
        photoUrl: sportPhotoUrl(sport),
    };
}

function templateToForm(template: PrivateClubTemplateAdminDto): TemplateForm {
    return {
        title: template.title,
        sportFilterId: template.sportId == null ? '' : String(template.sportId),
        sportFilterLabel: template.sportId == null ? '' : `Sport ${template.sportId}`,
        sportFilterPhotoUrl: '',
        sportLeagueId: template.sportLeagueId == null ? '' : String(template.sportLeagueId),
        sportLeagueLabel: template.sportLeagueId == null ? '' : `League #${template.sportLeagueId}`,
        sportLeaguePhotoUrl: '',
        scorePointMultiplier: String(template.scorePointMultiplier ?? 10),
        maxPicksPerMatch: String(template.maxPicksPerMatch ?? 2),
        allowedBetTypeGroupIds: (template.allowedBetTypeGroupIds ?? defaultAllowedBetTypeGroupIds).filter(id => Number.isFinite(id)),
        agentInstructions: template.agentInstructions ?? '',
        agenticMode: template.agenticMode,
        status: template.status,
        willSettleAt: toDateTimeLocal(template.willSettleAt),
        promoMetadataRows: Object.entries(template.promoMetadata ?? {}).map(([key, value]) => ({
            uiId: uiId(),
            key,
            value,
        })),
        rounds: template.rounds.map(round => ({
            uiId: uiId(),
            id: round.id,
            index: String(round.index),
            startsAt: toDateTimeLocal(round.startsAt),
            endsAt: toDateTimeLocal(round.endsAt),
            status: round.status,
            hasPicks: round.hasPicks,
            matches: round.matches.map(match => ({
                uiId: uiId(),
                matchId: String(match.matchId),
                allowedBetTypeGroupIds: (match.allowedBetTypeGroupIds ?? []).filter(id => Number.isFinite(id)),
                settlementStatus: match.settlementStatus,
            })),
        })),
    };
}

function LeagueSearchField(props: {
    selectedSportId: string;
    selectedSportLabel: string;
    selectedSportPhotoUrl: string;
    selectedLeagueId: string;
    selectedLeagueLabel: string;
    selectedLeaguePhotoUrl: string;
    selectedScheduledMatchesCount: number | null;
    sports: SportOption[];
    sportsLoading: boolean;
    onChooseSport: (sport: SportOption) => void;
    onClearSport: () => void;
    onChoose: (league: LeagueInfoDto) => void;
    onClear: () => void;
}) {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [options, setOptions] = React.useState<LeagueInfoDto[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        setSearchTerm('');
        setOptions([]);
    }, [props.selectedSportId]);

    React.useEffect(() => {
        const selectedSportId = Number(props.selectedSportId);
        if (props.selectedLeagueId || !Number.isFinite(selectedSportId) || selectedSportId <= 0) {
            setOptions([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const effectiveSearchTerm = searchTerm.trim().length >= 2 ? searchTerm.trim() : undefined;
        getPrivateClubLeagueCandidates(selectedSportId, effectiveSearchTerm, 20).then(result => {
            if (cancelled) return;
            setOptions(result ?? []);
            setLoading(false);
        }).catch(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [searchTerm, props.selectedSportId, props.selectedLeagueId]);

    const selectedSport = props.selectedSportId
        ? {
            id: Number(props.selectedSportId),
            name: props.selectedSportLabel || `Sport ${props.selectedSportId}`,
            photoUrl: props.selectedSportPhotoUrl || null,
        }
        : null;

    return (
        <Stack gap={1}>
            <Autocomplete
                options={props.sports}
                loading={props.sportsLoading}
                value={selectedSport}
                onChange={(_, value) => {
                    if (value) props.onChooseSport(value);
                    else props.onClearSport();
                }}
                getOptionLabel={option => sportLabel(option)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderOption={(optionProps, option) => (
                    <Box component="li" {...optionProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Avatar src={option.photoUrl ?? undefined} sx={{ width: 28, height: 28 }} />
                        <Typography variant="body2">{sportLabel(option)}</Typography>
                    </Box>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Sport"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedSport?.photoUrl ? (
                                <Avatar src={selectedSport.photoUrl} sx={{ width: 24, height: 24, mr: 1 }} />
                            ) : params.InputProps.startAdornment,
                            endAdornment: (
                                <>
                                    {props.sportsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            {props.selectedSportId && (
                <Button size="small" variant="outlined" color="error" onClick={props.onClearSport} sx={{ alignSelf: 'flex-start' }}>
                    Clear sport
                </Button>
            )}
            {props.selectedLeagueId ? (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Avatar src={props.selectedLeaguePhotoUrl || undefined} sx={{ width: 28, height: 28 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {props.selectedLeagueLabel || `League #${props.selectedLeagueId}`}
                    </Typography>
                    {props.selectedScheduledMatchesCount != null && (
                        <Chip
                            size="small"
                            color={props.selectedScheduledMatchesCount > 0 ? 'success' : 'warning'}
                            label={`${props.selectedScheduledMatchesCount} scheduled`}
                        />
                    )}
                    <Button size="small" color="error" variant="outlined" onClick={props.onClear}>Clear league</Button>
                </Box>
            ) : (
                <Autocomplete
                    options={options}
                    loading={loading}
                    disabled={!props.selectedSportId}
                    inputValue={searchTerm}
                    onInputChange={(_, value) => setSearchTerm(value)}
                    onChange={(_, value) => {
                        if (value) {
                            props.onChoose(value);
                            setSearchTerm('');
                            setOptions([]);
                        }
                    }}
                    getOptionLabel={option => leagueLabel(option)}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderOption={(optionProps, option) => (
                        <Box component="li" {...optionProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Avatar src={leaguePhotoUrl(option) ?? undefined} sx={{ width: 28, height: 28 }} />
                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                <Typography variant="body2" noWrap>{`[${option.id}] ${option.name ?? `League ${option.id}`}`}</Typography>
                                {option.sport && (
                                    <Stack direction="row" gap={0.75} alignItems="center">
                                        <Avatar src={sportPhotoUrl(option.sport) ?? undefined} sx={{ width: 18, height: 18 }} />
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {option.sport.name}
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>
                            <Chip
                                size="small"
                                color={(option.scheduledMatchesCount ?? 0) > 0 ? 'success' : 'warning'}
                                label={`${option.scheduledMatchesCount ?? 0} scheduled`}
                            />
                        </Box>
                    )}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Search sport league"
                            placeholder={props.selectedSportId ? 'Top scheduled leagues or type to search' : 'Select sport first'}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            )}
        </Stack>
    );
}

function formToPayload(form: TemplateForm): UpsertPrivateClubTemplateAdminDto {
    const title = form.title.trim();
    if (!title) throw new Error('Title is required.');
    validateTemplateStatusForSave(form);

    const sportIdText = form.sportFilterId.trim();
    const parsedSportId = sportIdText ? Number(sportIdText) : null;
    if (parsedSportId != null && (!Number.isFinite(parsedSportId) || parsedSportId <= 0)) {
        throw new Error('Sport must be selected.');
    }
    if (form.rounds.length > 0 && parsedSportId == null) {
        throw new Error('Sport must be selected before adding rounds.');
    }

    const sportLeagueIdText = form.sportLeagueId.trim();
    const parsedSportLeagueId = sportLeagueIdText ? Number(sportLeagueIdText) : null;
    if (parsedSportLeagueId != null && (!Number.isFinite(parsedSportLeagueId) || parsedSportLeagueId <= 0)) {
        throw new Error('Sport league ID must be a positive number.');
    }
    const scorePointMultiplier = Number(form.scorePointMultiplier);
    if (!Number.isFinite(scorePointMultiplier) || scorePointMultiplier <= 0) {
        throw new Error('Score point multiplier must be greater than zero.');
    }
    const maxPicksPerMatch = Number(form.maxPicksPerMatch);
    if (!Number.isInteger(maxPicksPerMatch) || maxPicksPerMatch < 1 || maxPicksPerMatch > 4) {
        throw new Error('Max picks per match must be a whole number between 1 and 4.');
    }
    const allowedBetTypeGroupIds = Array.from(new Set(form.allowedBetTypeGroupIds))
        .filter(id => Number.isFinite(id) && id > 0)
        .sort((a, b) => a - b);
    if (allowedBetTypeGroupIds.length === 0) {
        throw new Error('At least one allowed bet group must be selected.');
    }

    const promoMetadata: Record<string, string> = {};
    form.promoMetadataRows.forEach((row, index) => {
        const key = row.key.trim();
        const value = row.value.trim();
        if (!key && !value) return;
        if (!key) throw new Error(`Promo metadata row ${index + 1} requires a key.`);
        promoMetadata[key] = value;
    });

    const rounds = form.rounds.map((round, roundIndex) => {
        const index = Number(round.index);
        if (!Number.isInteger(index) || index <= 0) {
            throw new Error(`Round ${roundIndex + 1} index must be a positive integer.`);
        }
        const startsAt = fromDateTimeLocal(round.startsAt, `Round ${roundIndex + 1} start`);
        const endsAt = fromDateTimeLocal(round.endsAt, `Round ${roundIndex + 1} end`);
        if (new Date(startsAt).getTime() >= new Date(endsAt).getTime()) {
            throw new Error(`Round ${index} start must be before its end.`);
        }
        return {
            id: round.id || null,
            index,
            startsAt,
            endsAt,
            matches: round.matches.map((match, matchIndex) => {
                const matchId = Number(match.matchId);
                if (!Number.isFinite(matchId) || matchId <= 0) {
                    throw new Error(`Round ${roundIndex + 1}, match ${matchIndex + 1} requires a positive match id.`);
                }
                return {
                    matchId,
                    allowedBetTypeGroupIds,
                    settlementStatus: match.settlementStatus,
                };
            }),
        };
    });

    const duplicateRoundIndexes = rounds
        .map(round => round.index)
        .filter((roundIndex, index, values) => values.indexOf(roundIndex) !== index);
    if (duplicateRoundIndexes.length > 0) {
        throw new Error(`Round indexes must be unique: ${Array.from(new Set(duplicateRoundIndexes)).join(', ')}.`);
    }

    const sortedRoundRanges = rounds
        .map(round => ({
            index: round.index,
            startsAt: new Date(round.startsAt),
            endsAt: new Date(round.endsAt),
        }))
        .sort((left, right) => left.startsAt.getTime() - right.startsAt.getTime());
    for (let index = 1; index < sortedRoundRanges.length; index += 1) {
        const previous = sortedRoundRanges[index - 1];
        const current = sortedRoundRanges[index];
        if (current.startsAt.getTime() < previous.endsAt.getTime()) {
            throw new Error(`Round ${previous.index} overlaps round ${current.index}.`);
        }
    }

    return {
        title,
        sportId: parsedSportId,
        sportLeagueId: parsedSportLeagueId,
        scorePointMultiplier,
        maxPicksPerMatch,
        allowedBetTypeGroupIds,
        agentInstructions: form.agenticMode ? form.agentInstructions.trim() || null : null,
        agenticMode: form.agenticMode,
        status: form.status,
        willSettleAt: form.willSettleAt.trim() ? fromDateTimeLocal(form.willSettleAt, 'Will settle at') : null,
        promoMetadata,
        rounds,
    };
}

export default function PrivateClubTemplatesPage() {
    const navigate = useNavigate();
    const [templates, setTemplates] = React.useState<PrivateClubTemplateAdminDto[]>([]);
    const [statusFilter, setStatusFilter] = React.useState<PrivateClubTemplateStatus | ''>('');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [message, setMessage] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [dialogError, setDialogError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [editingTemplate, setEditingTemplate] = React.useState<PrivateClubTemplateAdminDto | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [form, setForm] = React.useState<TemplateForm>(emptyForm);
    const [leagueSchedule, setLeagueSchedule] = React.useState<LeagueScheduleState | null>(null);
    const [sports, setSports] = React.useState<SportOption[]>([]);
    const [sportsLoading, setSportsLoading] = React.useState(false);
    const [betTypeGroups, setBetTypeGroups] = React.useState<PrivateClubBetTypeGroupOptionAdminDto[]>([]);
    const [betTypeGroupsLoading, setBetTypeGroupsLoading] = React.useState(false);
    const [prefillingRound, setPrefillingRound] = React.useState(false);
    const [matchDetailsById, setMatchDetailsById] = React.useState<Record<number, MatchInfoDto | null>>({});
    const [matchBetGroupsById, setMatchBetGroupsById] = React.useState<Record<number, PrivateClubBetGroupAvailabilityDto[] | null>>({});
    const [expandedRoundMatches, setExpandedRoundMatches] = React.useState<Record<string, boolean>>({});
    const [agenticSettingsOpen, setAgenticSettingsOpen] = React.useState(false);
    const [agenticSettingsSaving, setAgenticSettingsSaving] = React.useState(false);
    const [agenticSettingsForm, setAgenticSettingsForm] = React.useState<AgenticSettingsForm | null>(null);
    const [agenticModalOpen, setAgenticModalOpen] = React.useState(false);
    const [agenticTab, setAgenticTab] = React.useState(0);
    const [agenticEvents, setAgenticEvents] = React.useState<PrivateClubAgenticEventAdminDto[]>([]);
    const [agenticLogs, setAgenticLogs] = React.useState<PrivateClubAgenticLogAdminDto[]>([]);
    const [agenticUnreadCount, setAgenticUnreadCount] = React.useState(0);
    const [agenticLoading, setAgenticLoading] = React.useState(false);
    const [agenticRuns, setAgenticRuns] = React.useState<AgentRunAdminDto[]>([]);
    const [agentConfirmation, setAgentConfirmation] = React.useState<AgentConfirmation | null>(null);
    const defaultSport = React.useMemo(() => findDefaultSport(sports), [sports]);

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        const rows = await getPrivateClubTemplates(statusFilter || undefined);
        setTemplates(rows ?? []);
        setLoading(false);
        if (!rows) setError('Failed to load private club templates.');
    }, [statusFilter]);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        let cancelled = false;
        setBetTypeGroupsLoading(true);
        getPrivateClubBetTypeGroups()
            .then(groups => {
                if (!cancelled) setBetTypeGroups(groups);
            })
            .finally(() => {
                if (!cancelled) setBetTypeGroupsLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const loadAgenticRecords = React.useCallback(async () => {
        setAgenticLoading(true);
        const [eventsResult, logsResult] = await Promise.all([
            getPrivateClubAgenticEvents(0, 50),
            getPrivateClubAgenticLogs(0, 50),
        ]);
        setAgenticEvents(eventsResult?.events.items ?? []);
        setAgenticUnreadCount(eventsResult?.unreadCount ?? 0);
        setAgenticLogs(logsResult?.items ?? []);
        setAgenticLoading(false);
    }, []);

    React.useEffect(() => {
        loadAgenticRecords();
    }, [loadAgenticRecords]);

    const loadAgenticRuns = React.useCallback(async () => {
        const runs = await getPrivateClubAgenticRuns();
        setAgenticRuns(runs ?? []);
    }, []);

    React.useEffect(() => {
        loadAgenticRuns();
        const intervalId = window.setInterval(loadAgenticRuns, 5000);
        return () => window.clearInterval(intervalId);
    }, [loadAgenticRuns]);

    React.useEffect(() => {
        let cancelled = false;
        setSportsLoading(true);
        getPaginatedSports(0, 500).then(result => {
            if (cancelled) return;
            setSports((result?.items ?? []).map(sportEntityToOption));
            setSportsLoading(false);
        }).catch(() => {
            if (!cancelled) setSportsLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    React.useEffect(() => {
        setPage(0);
    }, [statusFilter]);

    React.useEffect(() => {
        if (!dialogOpen || editingTemplate || !defaultSport) return;
        setForm(prev => prev.sportFilterId ? prev : withSelectedSport(prev, defaultSport));
    }, [dialogOpen, editingTemplate, defaultSport]);

    React.useEffect(() => {
        if (!dialogOpen || !form.sportLeagueId) {
            setLeagueSchedule(null);
            return;
        }
        const leagueId = Number(form.sportLeagueId);
        if (!Number.isFinite(leagueId) || leagueId <= 0) {
            setLeagueSchedule(null);
            return;
        }
        let cancelled = false;
        setLeagueSchedule({ loading: true, count: null, error: false });
        getLeagueScheduledMatches(leagueId).then(result => {
            if (cancelled) return;
            setLeagueSchedule({
                loading: false,
                count: result?.scheduledMatchesCount ?? 0,
                error: false,
            });
        }).catch(() => {
            if (!cancelled) setLeagueSchedule({ loading: false, count: null, error: true });
        });
        return () => { cancelled = true; };
    }, [dialogOpen, form.sportLeagueId]);

    const visibleTemplates = React.useMemo(() => {
        return [...templates].sort((a, b) => a.title.localeCompare(b.title));
    }, [templates]);

    const pagedTemplates = visibleTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const roundMatchIds = React.useMemo(() => {
        return Array.from(new Set(
            form.rounds
                .flatMap(round => round.matches)
                .map(match => Number(match.matchId))
                .filter(matchId => Number.isFinite(matchId) && matchId > 0)
        ));
    }, [form.rounds]);

    React.useEffect(() => {
        if (!dialogOpen || roundMatchIds.length === 0) return;
        const missingMatchIds = roundMatchIds.filter(matchId =>
            !Object.prototype.hasOwnProperty.call(matchDetailsById, matchId)
        );
        if (missingMatchIds.length === 0) return;

        let cancelled = false;
        Promise.all(missingMatchIds.map(matchId =>
            getPrivateClubMatchDetails(matchId)
                .then(match => [matchId, match] as const)
                .catch(() => [matchId, null] as const)
        )).then(results => {
            if (cancelled) return;
            setMatchDetailsById(prev => {
                const next = { ...prev };
                results.forEach(([matchId, match]) => {
                    next[matchId] = match;
                });
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [dialogOpen, roundMatchIds, matchDetailsById]);

    React.useEffect(() => {
        if (!dialogOpen || roundMatchIds.length === 0) return;
        const missingMatchIds = roundMatchIds.filter(matchId =>
            !Object.prototype.hasOwnProperty.call(matchBetGroupsById, matchId)
        );
        if (missingMatchIds.length === 0) return;

        let cancelled = false;
        Promise.all(missingMatchIds.map(matchId =>
            getPrivateClubMatchBetGroups(matchId)
                .then(groups => [matchId, groups] as const)
                .catch(() => [matchId, null] as const)
        )).then(results => {
            if (cancelled) return;
            setMatchBetGroupsById(prev => {
                const next = { ...prev };
                results.forEach(([matchId, groups]) => {
                    next[matchId] = groups;
                });
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [dialogOpen, roundMatchIds, matchBetGroupsById]);

    const patchRound = (roundUiId: string, patch: Partial<RoundForm>) => {
        setForm(prev => ({
            ...prev,
            rounds: prev.rounds.map(round => round.uiId === roundUiId ? { ...round, ...patch } : round),
        }));
    };

    const patchMatch = (roundUiId: string, matchUiId: string, patch: Partial<MatchForm>) => {
        setForm(prev => ({
            ...prev,
            rounds: prev.rounds.map(round => round.uiId === roundUiId
                ? { ...round, matches: round.matches.map(match => match.uiId === matchUiId ? { ...match, ...patch } : match) }
                : round),
        }));
    };

    const addRound = async () => {
        setDialogError(null);
        setMessage(null);
        const sportId = Number(form.sportFilterId);
        if (!Number.isFinite(sportId) || sportId <= 0) {
            setDialogError('Sport must be selected before adding rounds.');
            return;
        }

        const round = prefilledRound(form.rounds);
        const leagueId = form.sportLeagueId ? Number(form.sportLeagueId) : null;
        let matches: MatchForm[] = [];

        if (leagueId != null && Number.isFinite(leagueId) && leagueId > 0) {
            setPrefillingRound(true);
            const result = await getPrivateClubScheduledMatches(
                sportId,
                new Date(fromDateTimeLocal(round.startsAt, 'Round start')).getTime(),
                new Date(fromDateTimeLocal(round.endsAt, 'Round end')).getTime(),
                leagueId,
                10,
            ).catch(() => null);
            setPrefillingRound(false);

            if (!result) {
                setDialogError('Failed to prefill round matches.');
                return;
            }
            const prefilledMatches = result.items.slice(0, 10);
            setMatchDetailsById(prev => {
                const next = { ...prev };
                prefilledMatches.forEach(match => {
                    next[match.id] = match;
                });
                return next;
            });
            setMatchBetGroupsById(prev => {
                const next = { ...prev };
                prefilledMatches.forEach(match => {
                    next[match.id] = match.betGroups ?? [];
                });
                return next;
            });
            matches = prefilledMatches.map(match => ({
                ...emptyMatch(),
                matchId: String(match.id),
                allowedBetTypeGroupIds: form.allowedBetTypeGroupIds,
            }));
            setMessage(matches.length > 0
                ? `Round prefilled with ${matches.length} scheduled match${matches.length === 1 ? '' : 'es'}.`
                : 'Round added without matches because none were scheduled in the selected interval.');
        }

        setForm(prev => ({
            ...prev,
            rounds: [...prev.rounds, { ...round, matches }],
        }));
        setExpandedRoundMatches(prev => ({ ...prev, [round.uiId]: true }));
    };

    const openCreate = () => {
        setError(null);
        setDialogError(null);
        setMessage(null);
        setSaving(false);
        setEditingTemplate(null);
        setForm(defaultSport ? withSelectedSport(emptyForm(), defaultSport) : emptyForm());
        setMatchDetailsById({});
        setMatchBetGroupsById({});
        setExpandedRoundMatches({});
        setDialogOpen(true);
    };

    const openEdit = (template: PrivateClubTemplateAdminDto) => {
        setError(null);
        setDialogError(null);
        setMessage(null);
        setSaving(false);
        setEditingTemplate(template);
        const templateForm = templateToForm(template);
        const sport = template.sportId == null ? null : sports.find(item => item.id === template.sportId) ?? null;
        setMatchDetailsById({});
        setMatchBetGroupsById({});
        setExpandedRoundMatches({});
        setForm({
            ...templateForm,
            sportFilterLabel: sport ? sportLabel(sport) : templateForm.sportFilterLabel,
            sportFilterPhotoUrl: sport?.photoUrl ?? templateForm.sportFilterPhotoUrl,
        });
        setDialogOpen(true);
        if (template.sportLeagueId != null) {
            getLeagueById(template.sportLeagueId).then(league => {
                if (!league) return;
                const sportOption = leagueSportToOption(league.sport);
                setForm(prev => prev.sportLeagueId === String(league.id)
                    ? {
                        ...prev,
                        sportLeagueLabel: leagueLabel(league),
                        sportLeaguePhotoUrl: leaguePhotoUrl(league) ?? '',
                        sportFilterId: sportOption ? String(sportOption.id) : prev.sportFilterId,
                        sportFilterLabel: sportOption ? sportLabel(sportOption) : prev.sportFilterLabel,
                        sportFilterPhotoUrl: sportOption?.photoUrl ?? prev.sportFilterPhotoUrl,
                    }
                    : prev);
            });
        }
    };

    const saveTemplate = async () => {
        if (saving) return;
        setDialogError(null);
        setMessage(null);
        let payload: UpsertPrivateClubTemplateAdminDto;
        try {
            payload = formToPayload(form);
        } catch (e) {
            setDialogError(e instanceof Error ? e.message : 'Template form is invalid.');
            return;
        }

        setSaving(true);
        try {
            const result = editingTemplate
                ? await updatePrivateClubTemplate(editingTemplate.id, payload)
                : await createPrivateClubTemplate(payload);
            if (!result.ok) {
                setDialogError(result.error ?? 'Template was not saved.');
                return;
            }
            setDialogOpen(false);
            setDialogError(null);
            setMessage(editingTemplate ? 'Template updated.' : 'Template created.');
            await load();
        } finally {
            setSaving(false);
        }
    };

    const deleteTemplate = async (template: PrivateClubTemplateAdminDto) => {
        if (!window.confirm(`Delete private club template "${template.title}"?`)) return;
        setError(null);
        setMessage(null);
        const result = await deletePrivateClubTemplate(template.id);
        if (!result.ok) {
            setError(result.error ?? 'Template was not deleted.');
            return;
        }
        setMessage('Template deleted.');
        await load();
    };

    const cancelRound = async (round: RoundForm) => {
        if (!editingTemplate || !round.id || round.status === 'CANCELLED') return;
        if (!window.confirm(`Cancel round ${round.index}? All picks for this round will be cancelled.`)) return;
        setDialogError(null);
        const result = await cancelPrivateClubRound(editingTemplate.id, round.id);
        if (!result.ok || !result.data) {
            setDialogError(result.error ?? 'Round was not cancelled.');
            return;
        }
        setEditingTemplate(result.data);
        setForm(templateToForm(result.data));
        setMessage(`Round ${round.index} cancelled.`);
        await load();
    };

    const openAgenticSettings = async () => {
        setError(null);
        setDialogError(null);
        const config = await getPrivateClubAgenticConfiguration();
        if (!config) {
            setError('Failed to load private club agent settings.');
            return;
        }
        setAgenticSettingsForm(agenticSettingsToForm(config, sports));
        setAgenticSettingsOpen(true);
    };

    const saveAgenticSettings = async () => {
        if (!agenticSettingsForm || agenticSettingsSaving) return;
        setAgenticSettingsSaving(true);
        try {
            const payload = agenticSettingsPayload(agenticSettingsForm);
            const updated = await updatePrivateClubAgenticConfiguration(payload);
            if (!updated) {
                setDialogError('Private club agent settings were not saved.');
                return;
            }
            setAgenticSettingsForm(agenticSettingsToForm(updated, sports));
            setAgenticSettingsOpen(false);
            setDialogError(null);
            setMessage('Private club agent settings saved.');
        } catch (e) {
            setDialogError(e instanceof Error ? e.message : 'Private club agent settings are invalid.');
        } finally {
            setAgenticSettingsSaving(false);
        }
    };

    const openAgenticModal = async () => {
        setAgenticModalOpen(true);
        await loadAgenticRecords();
    };

    const markEventRead = async (eventId: string) => {
        const updated = await markPrivateClubAgenticEventRead(eventId);
        if (!updated) return;
        await loadAgenticRecords();
    };

    const markAllEventsRead = async () => {
        await markAllPrivateClubAgenticEventsRead();
        await loadAgenticRecords();
    };

    const runTemplateAgent = async (template: PrivateClubTemplateAdminDto) => {
        setMessage(null);
        setError(null);
        const result = await runPrivateClubTemplateAgent(template.id);
        if (!result) {
            setError('Agent run failed.');
            return;
        }
        if (result.runningRun) {
            setAgenticRuns([result.runningRun]);
            setMessage(`Agent is already running: ${result.runningRun.description}`);
            return;
        }
        if (template.agenticRoundGenerationFailedAt && result.createdRounds === 0) {
            setError('The agent did not create a round. Automatic generation remains stopped for this template.');
        } else {
            setMessage(`Agent processed ${result.processedTemplates} template(s), created ${result.createdRounds} round(s).`);
        }
        await Promise.all([load(), loadAgenticRecords(), loadAgenticRuns()]);
    };

    const runTemplateCreation = async () => {
        setMessage(null);
        setError(null);
        const result = await runPrivateClubAgenticTemplateCreation();
        if (!result) {
            setError('Agent template creation run failed.');
            return;
        }
        if (result.runningRun) {
            setAgenticRuns([result.runningRun]);
            setMessage(`Agent is already running: ${result.runningRun.description}`);
            return;
        }
        setMessage(`Agent created ${result.createdTemplates} template(s).`);
        await Promise.all([load(), loadAgenticRecords(), loadAgenticRuns()]);
    };

    const confirmAgentAction = () => {
        const confirmation = agentConfirmation;
        setAgentConfirmation(null);
        if (!confirmation) return;
        if (confirmation.action === 'GENERATE_TEMPLATES') {
            void runTemplateCreation();
            return;
        }
        void runTemplateAgent(confirmation.template);
    };

    const activeAgentRun = agenticRuns[0] ?? null;
    const activeAgentRunTooltip = activeAgentRun
        ? `${activeAgentRun.description} Started ${formatDate(activeAgentRun.startedAt)}.`
        : '';

    return (
        <Stack gap={2} sx={{ mx: 4, my: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography variant="h4">Private club templates</Typography>
                <Stack direction="row" gap={1} alignItems="center">
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as PrivateClubTemplateStatus | '')}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                            <MenuItem value="SETTLED">SETTLED</MenuItem>
                            <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Private club agent settings">
                        <IconButton onClick={openAgenticSettings}>
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Agent logs and events">
                        <IconButton onClick={openAgenticModal}>
                            <Badge color="error" badgeContent={agenticUnreadCount}>
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={activeAgentRun ? activeAgentRunTooltip : 'Run AI template creation'}>
                        <span>
                            <IconButton
                                onClick={() => setAgentConfirmation({ action: 'GENERATE_TEMPLATES' })}
                                disabled={Boolean(activeAgentRun)}
                            >
                                {activeAgentRun ? <HourglassEmptyIcon /> : <PlayArrowIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New template</Button>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton onClick={load} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Template</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>League</TableCell>
                        <TableCell>Rounds</TableCell>
                        <TableCell>Clubs count</TableCell>
                        <TableCell>Bets placed</TableCell>
                        <TableCell>Will settle at deadline</TableCell>
                        <TableCell>Next round</TableCell>
                        <TableCell>Warnings</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell colSpan={10} align="center">Loading templates...</TableCell>
                        </TableRow>
                    )}
                    {!loading && pagedTemplates.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={10} align="center">No private club templates found</TableCell>
                        </TableRow>
                    )}
                    {!loading && pagedTemplates.map(template => (
                        <TableRow key={template.id}>
                            <TableCell>
                                <Stack spacing={0.25}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => navigate(`/private-clubs/templates/${template.id}/clubs`)}
                                        sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                                    >
                                        {template.title}
                                    </Button>
                                    <Typography variant="caption" color="text.secondary">{template.id}</Typography>
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Chip size="small" label={template.status} color={statusColor(template.status)} />
                            </TableCell>
                            <TableCell>
                                {template.sportLeagueId == null ? '-' : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Avatar
                                            src={template.sportLeague ? leaguePhotoUrl(template.sportLeague) || undefined : undefined}
                                            sx={{ width: 24, height: 24 }}
                                        />
                                        <Stack spacing={0}>
                                            <Typography variant="body2">
                                                {template.sportLeague?.name || `League #${template.sportLeagueId}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                #{template.sportLeagueId}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                )}
                            </TableCell>
                            <TableCell>{template.rounds.length}</TableCell>
                            <TableCell>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => navigate(`/private-clubs/templates/${template.id}/clubs`)}
                                >
                                    {template.clubsCount}
                                </Button>
                            </TableCell>
                            <TableCell>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => navigate(`/private-clubs/templates/${template.id}/placed-bets`)}
                                >
                                    {template.placedBetsCount}
                                </Button>
                            </TableCell>
                            <TableCell>{formatDate(template.willSettleAt)}</TableCell>
                            <TableCell>
                                <Stack spacing={0.25}>
                                    <span>{formatDate(template.nextRoundStart)}</span>
                                    <Typography variant="caption" color="text.secondary">{formatDate(template.nextRoundEnd)}</Typography>
                                </Stack>
                            </TableCell>
                            <TableCell>
                                <Stack gap={0.5} alignItems="flex-start">
                                    <Stack direction="row" gap={0.5} flexWrap="wrap">
                                        {template.scheduleWarnings.map(warning => (
                                            <Chip key={warning} size="small" color="warning" label={warning} />
                                        ))}
                                        {template.agenticMode && template.agenticRoundGenerationFailedAt && (
                                            <Tooltip title={`${template.agenticRoundGenerationFailureReason || 'Round generation failed'} (${formatDate(template.agenticRoundGenerationFailedAt)})`}>
                                                <Chip
                                                    size="small"
                                                    color="error"
                                                    icon={<ErrorOutlineIcon />}
                                                    label="Auto agent stopped"
                                                />
                                            </Tooltip>
                                        )}
                                        {template.scheduleWarnings.length === 0 && !template.agenticRoundGenerationFailedAt && '-'}
                                    </Stack>
                                    {template.agenticMode && template.agenticRoundGenerationFailedAt && (
                                        <Typography variant="caption" color="error.main" sx={{ maxWidth: 260 }}>
                                            Fix the problem, then run the agent manually to resume automation.
                                        </Typography>
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell align="right">
                                {template.agenticMode && (
                                    <Tooltip title={activeAgentRun
                                        ? activeAgentRunTooltip
                                        : template.agenticRoundGenerationFailedAt
                                            ? 'Retry manually after fixing the generation problem'
                                            : 'Run agent for this template'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => setAgentConfirmation({ action: 'GENERATE_ROUNDS', template })}
                                                disabled={Boolean(activeAgentRun) || ['SETTLED', 'CANCELLED'].includes(template.status)}
                                            >
                                                {activeAgentRun ? <HourglassEmptyIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        onClick={() => openEdit(template)}
                                        disabled={['SETTLED', 'CANCELLED'].includes(template.status)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => deleteTemplate(template)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                component="div"
                count={visibleTemplates.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={event => {
                    setRowsPerPage(Number(event.target.value));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
            />

            <Dialog open={agenticSettingsOpen} onClose={() => setAgenticSettingsOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Private club agent settings</DialogTitle>
                <DialogContent>
                    {dialogError && <Alert severity="error" onClose={() => setDialogError(null)} sx={{ mt: 1, mb: 2 }}>{dialogError}</Alert>}
                    {agenticSettingsForm && (
                        <Stack gap={2} sx={{ mt: 1 }}>
                            <Alert severity="info">
                                The agent's AI runs in the AI hub: edit its prompt and model, and see its runs and cost, in{' '}
                                <Link component={RouterLink} to={'/ai/agents/' + ROUND_PLANNER_AGENT_CODE}>AI › Agents › {ROUND_PLANNER_AGENT_CODE}</Link>
                                {' '}(round decisions) and{' '}
                                <Link component={RouterLink} to={'/ai/tasks/' + SELECT_MATCHES_TASK_CODE}>AI › Tasks › {SELECT_MATCHES_TASK_CODE}</Link>
                                {' '}(match selection).
                            </Alert>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agenticSettingsForm.automaticRoundCreationEnabled}
                                        onChange={e => setAgenticSettingsForm(prev => prev && ({
                                            ...prev,
                                            automaticRoundCreationEnabled: e.target.checked,
                                        }))}
                                    />
                                }
                                label={agenticSettingLabel(
                                    'Allow AI to automatically create new rounds',
                                    'When disabled, scheduled agent runs do not create rounds. Administrators can still run the agent manually for a template.',
                                )}
                            />
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <TextField label={agenticSettingLabel('Run interval min', 'Target frequency for scheduled agentic round-management checks. Cron fixed delay is still controlled by server properties.')} value={agenticSettingsForm.scheduleRunIntervalMinutes} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, scheduleRunIntervalMinutes: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Low runway hours', 'How soon before the latest playable round ends the agent should prepare another scheduled round.')} value={agenticSettingsForm.lowRunwayHours} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, lowRunwayHours: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Open lead hours', 'Default time between a generated round opening and the first selected match starting.')} value={agenticSettingsForm.defaultOpenLeadHours} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, defaultOpenLeadHours: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Round match limit', 'Maximum number of matches the agent should include in one generated round.')} value={agenticSettingsForm.defaultRoundMatchLimit} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, defaultRoundMatchLimit: e.target.value }))} fullWidth />
                            </Stack>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={agenticSettingsForm.templateAutoGenerationEnabled}
                                        onChange={e => setAgenticSettingsForm(prev => prev && ({
                                            ...prev,
                                            templateAutoGenerationEnabled: e.target.checked,
                                        }))}
                                    />
                                }
                                label={agenticSettingLabel(
                                    'Allow AI to create private club templates',
                                    'When enabled, the agent may create new active templates for allowed sports while respecting active-template and duplicate-match limits.',
                                )}
                            />
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <Autocomplete
                                    multiple
                                    options={sports}
                                    loading={sportsLoading}
                                    value={agenticSettingsForm.templateAutoGenerationAllowedSports}
                                    onChange={(_, value) => setAgenticSettingsForm(prev => prev && ({
                                        ...prev,
                                        templateAutoGenerationAllowedSports: value,
                                    }))}
                                    getOptionLabel={option => sportLabel(option)}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    renderOption={(optionProps, option) => (
                                        <Box component="li" {...optionProps} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Avatar src={option.photoUrl ?? undefined} sx={{ width: 28, height: 28 }} />
                                            <Typography variant="body2">{sportLabel(option)}</Typography>
                                        </Box>
                                    )}
                                    renderTags={(value, getTagProps) => value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.id}
                                            size="small"
                                            avatar={option.photoUrl ? <Avatar src={option.photoUrl} /> : undefined}
                                            label={sportLabel(option)}
                                        />
                                    ))}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={agenticSettingLabel(
                                                'Allowed sports',
                                                'Sports where automatic template creation is allowed. Empty selection blocks new AI-created templates.',
                                            )}
                                            placeholder="Search sports"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {sportsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    fullWidth
                                />
                                <TextField label={agenticSettingLabel('Max active templates', 'Maximum number of active AI-created private club templates allowed at the same time.')} value={agenticSettingsForm.maxActiveTemplates} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, maxActiveTemplates: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Max templates per run', 'Maximum number of new templates the agent may create in a single template-generation run.')} value={agenticSettingsForm.maxTemplatesCreatedPerRun} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, maxTemplatesCreatedPerRun: e.target.value }))} fullWidth />
                            </Stack>
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                <TextField label={agenticSettingLabel('Creation lookahead hours', 'Future schedule window analyzed when the agent searches for matches for new templates.')} value={agenticSettingsForm.templateCreationLookaheadHours} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, templateCreationLookaheadHours: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Creation min matches', 'Minimum number of valid scheduled matches required before the agent can create a new template.')} value={agenticSettingsForm.templateCreationMinMatches} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, templateCreationMinMatches: e.target.value }))} fullWidth />
                                <TextField label={agenticSettingLabel('Manual warning runway', 'How soon before a non-agentic template runs out of playable rounds the monitor should raise a warning event.')} value={agenticSettingsForm.nonAgenticWarningRunwayHours} onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, nonAgenticWarningRunwayHours: e.target.value }))} fullWidth />
                            </Stack>
                            <TemplateBetGroupsSelect
                                value={agenticSettingsForm.templateAutoGenerationAllowedBetTypeGroupIds}
                                options={betTypeGroups}
                                loading={betTypeGroupsLoading}
                                onChange={templateAutoGenerationAllowedBetTypeGroupIds => setAgenticSettingsForm(prev => prev && ({
                                    ...prev,
                                    templateAutoGenerationAllowedBetTypeGroupIds,
                                }))}
                            />
                            <TextField
                                label={agenticSettingLabel(
                                    'Template creation rules',
                                    'Business rules the agent should apply when creating new templates, such as preferred leagues, exclusions, naming style, and duplicate coverage limits.',
                                )}
                                value={agenticSettingsForm.templateAutoGenerationRules}
                                onChange={e => setAgenticSettingsForm(prev => prev && ({ ...prev, templateAutoGenerationRules: e.target.value }))}
                                fullWidth
                                multiline
                                minRows={3}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAgenticSettingsOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveAgenticSettings} disabled={!agenticSettingsForm || agenticSettingsSaving}>
                        {agenticSettingsSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={agentConfirmation != null} onClose={() => setAgentConfirmation(null)} fullWidth maxWidth="sm">
                <DialogTitle>
                    {agentConfirmation?.action === 'GENERATE_TEMPLATES'
                        ? 'Generate private club templates'
                        : 'Generate rounds with AI'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {agentConfirmation?.action === 'GENERATE_TEMPLATES'
                            ? 'This action will invoke AI to generate templates based on the current configuration. Would you like to proceed?'
                            : `This action will invoke AI to generate rounds for template "${agentConfirmation?.template.title ?? ''}" based on the current configuration. Would you like to proceed?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAgentConfirmation(null)}>Cancel</Button>
                    <Button variant="contained" onClick={confirmAgentAction}>Proceed</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={agenticModalOpen} onClose={() => setAgenticModalOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <span>Private club agent</span>
                        <Button startIcon={<DoneAllIcon />} onClick={markAllEventsRead} disabled={agenticUnreadCount === 0}>
                            Read all
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Tabs value={agenticTab} onChange={(_, value) => setAgenticTab(value)} sx={{ mb: 2 }}>
                        <Tab label={`Events (${agenticUnreadCount} unread)`} />
                        <Tab label="Logs" />
                    </Tabs>
                    {agenticLoading && <Typography>Loading...</Typography>}
                    {!agenticLoading && agenticTab === 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Level</TableCell>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Template</TableCell>
                                    <TableCell>Message</TableCell>
                                    <TableCell>Created</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agenticEvents.length === 0 && (
                                    <TableRow><TableCell colSpan={6} align="center">No events</TableCell></TableRow>
                                )}
                                {agenticEvents.map(event => (
                                    <TableRow key={event.id} sx={{ opacity: event.read ? 0.65 : 1 }}>
                                        <TableCell width={40}>
                                            {!event.read && (
                                                <Tooltip title="Mark as read">
                                                    <IconButton size="small" onClick={() => markEventRead(event.id)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell><Chip size="small" label={event.level} color={event.level === 'ERROR' ? 'error' : event.level === 'WARNING' ? 'warning' : 'default'} /></TableCell>
                                        <TableCell>{event.eventType}</TableCell>
                                        <TableCell>
                                            {event.templateId ? (
                                                <Button size="small" onClick={() => navigate(`/private-clubs/templates/${event.templateId}/clubs`)}>
                                                    {event.templateName ?? event.templateId}
                                                </Button>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{event.message}</Typography>
                                            {formatDetails(event.details) && <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>{formatDetails(event.details)}</Typography>}
                                        </TableCell>
                                        <TableCell>{formatDate(event.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                    {!agenticLoading && agenticTab === 1 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Level</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Template</TableCell>
                                    <TableCell>Message</TableCell>
                                    <TableCell>Created</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {agenticLogs.length === 0 && (
                                    <TableRow><TableCell colSpan={5} align="center">No logs</TableCell></TableRow>
                                )}
                                {agenticLogs.map(log => (
                                    <TableRow key={log.id}>
                                        <TableCell><Chip size="small" label={log.level} color={log.level === 'ERROR' ? 'error' : log.level === 'WARNING' ? 'warning' : 'default'} /></TableCell>
                                        <TableCell>{log.eventType}</TableCell>
                                        <TableCell>
                                            {log.templateId ? (
                                                <Button size="small" onClick={() => navigate(`/private-clubs/templates/${log.templateId}/clubs`)}>
                                                    {log.templateName ?? log.templateId}
                                                </Button>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{log.message}</Typography>
                                            {formatDetails(log.details) && <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>{formatDetails(log.details)}</Typography>}
                                        </TableCell>
                                        <TableCell>{formatDate(log.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAgenticModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
                <DialogTitle>{editingTemplate ? 'Edit private club template' : 'Create private club template'}</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ mt: 1 }}>
                        {dialogError && <Alert severity="error" onClose={() => setDialogError(null)}>{dialogError}</Alert>}
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                            fullWidth
                        />
                        <Box sx={{ width: '100%' }}>
                            <LeagueSearchField
                                selectedSportId={form.sportFilterId}
                                selectedSportLabel={form.sportFilterLabel}
                                selectedSportPhotoUrl={form.sportFilterPhotoUrl}
                                selectedLeagueId={form.sportLeagueId}
                                selectedLeagueLabel={form.sportLeagueLabel}
                                selectedLeaguePhotoUrl={form.sportLeaguePhotoUrl}
                                selectedScheduledMatchesCount={leagueSchedule?.loading || leagueSchedule?.error ? null : leagueSchedule?.count ?? null}
                                sports={sports}
                                sportsLoading={sportsLoading}
                                onChooseSport={sport => setForm(prev => ({
                                    ...prev,
                                    sportFilterId: String(sport.id),
                                    sportFilterLabel: sportLabel(sport),
                                    sportFilterPhotoUrl: sport.photoUrl ?? '',
                                    sportLeagueId: '',
                                    sportLeagueLabel: '',
                                    sportLeaguePhotoUrl: '',
                                    rounds: prev.sportFilterId && prev.sportFilterId !== String(sport.id) ? [] : prev.rounds,
                                }))}
                                onClearSport={() => setForm(prev => ({
                                    ...prev,
                                    sportFilterId: '',
                                    sportFilterLabel: '',
                                    sportFilterPhotoUrl: '',
                                    sportLeagueId: '',
                                    sportLeagueLabel: '',
                                    sportLeaguePhotoUrl: '',
                                    rounds: [],
                                }))}
                                onChoose={league => setForm(prev => ({
                                    ...prev,
                                    sportLeagueId: String(league.id),
                                    sportLeagueLabel: leagueLabel(league),
                                    sportLeaguePhotoUrl: leaguePhotoUrl(league) ?? '',
                                }))}
                                onClear={() => setForm(prev => ({
                                    ...prev,
                                    sportLeagueId: '',
                                    sportLeagueLabel: '',
                                    sportLeaguePhotoUrl: '',
                                }))}
                            />
                            {leagueSchedule && (
                                <Alert
                                    severity={leagueSchedule.error ? 'error' : leagueSchedule.count === 0 ? 'warning' : 'success'}
                                    sx={{ mt: 1 }}
                                >
                                    {leagueSchedule.loading
                                        ? 'Checking scheduled matches...'
                                        : leagueSchedule.error
                                            ? 'Scheduled match count is unavailable.'
                                            : leagueSchedule.count === 0
                                                ? 'No scheduled matches in DB. A league-based private club cannot be settled from this league until scheduled matches are available.'
                                                : `${leagueSchedule.count} scheduled match${leagueSchedule.count === 1 ? '' : 'es'} in DB.`}
                                </Alert>
                            )}
                        </Box>
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={form.status}
                                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value as PrivateClubTemplateStatus }))}
                                >
                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                    <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                                    <MenuItem value="SETTLED">SETTLED</MenuItem>
                                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Will settle at"
                                type="datetime-local"
                                value={form.willSettleAt}
                                onChange={e => setForm(prev => ({ ...prev, willSettleAt: e.target.value }))}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText="Optional automatic settlement time. The template settles then only after every round is settled."
                            />
                            <TextField
                                label="Score point multiplier"
                                type="number"
                                value={form.scorePointMultiplier}
                                onChange={e => setForm(prev => ({ ...prev, scorePointMultiplier: e.target.value }))}
                                fullWidth
                                inputProps={{ min: 0.01, step: 0.01 }}
                                helperText="Score preview uses grabbed coefficient multiplied by this value. Default is 10."
                            />
                            <FormControl fullWidth>
                                <InputLabel>Max picks per match</InputLabel>
                                <Select
                                    label="Max picks per match"
                                    value={form.maxPicksPerMatch}
                                    onChange={e => setForm(prev => ({
                                        ...prev,
                                        maxPicksPerMatch: String(e.target.value),
                                    }))}
                                >
                                    {maxPicksPerMatchOptions.map(value => (
                                        <MenuItem key={value} value={String(value)}>{value}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <TemplateBetGroupsSelect
                            value={form.allowedBetTypeGroupIds}
                            options={betTypeGroups}
                            loading={betTypeGroupsLoading}
                            onChange={allowedBetTypeGroupIds => setForm(prev => ({
                                ...prev,
                                allowedBetTypeGroupIds,
                                rounds: prev.rounds.map(round => ({
                                    ...round,
                                    matches: round.matches.map(match => ({
                                        ...match,
                                        allowedBetTypeGroupIds,
                                    })),
                                })),
                            }))}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.agenticMode}
                                    onChange={e => setForm(prev => ({
                                        ...prev,
                                        agenticMode: e.target.checked,
                                        agentInstructions: e.target.checked ? prev.agentInstructions : '',
                                    }))}
                                />
                            }
                            label="Agentic mode"
                        />
                        <Typography variant="caption" color="text.secondary">
                            AI agent will run once there are no active rounds or current round is about to expire to create a new round basing on the provided instructions.
                        </Typography>
                        <TextField
                            label="Agent instructions"
                            value={form.agentInstructions}
                            onChange={e => setForm(prev => ({ ...prev, agentInstructions: e.target.value }))}
                            fullWidth
                            multiline
                            minRows={2}
                            disabled={!form.agenticMode}
                        />
                        {editingTemplate?.latestAgentDecision && (
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="caption" color="text.secondary">Latest agent decision</Typography>
                                    <Chip
                                        size="small"
                                        color={editingTemplate.latestAgentDecision === 'WAIT' ? 'info' : 'default'}
                                        label={editingTemplate.latestAgentDecision}
                                    />
                                    {editingTemplate.latestAgentDecisionAt && (
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(editingTemplate.latestAgentDecisionAt)}
                                        </Typography>
                                    )}
                                </Stack>
                                {editingTemplate.latestAgentDecisionReason && (
                                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                                        {editingTemplate.latestAgentDecisionReason}
                                    </Typography>
                                )}
                                {editingTemplate.latestAgentDecision === 'WAIT' && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                                        Automatic runs resume after the template has no scheduled, open, or processing rounds.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Divider />
                        <Stack gap={1.5}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">Rounds</Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={addRound}
                                    disabled={prefillingRound}
                                >
                                    {prefillingRound ? 'Prefilling...' : 'Add round'}
                                </Button>
                            </Stack>
                            {form.rounds.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No rounds</Typography>
                            )}
                            {form.rounds.map(round => {
                                const roundStatus = derivedRoundStatus(round);
                                const roundLocked = round.status === 'CANCELLED';
                                const structuralControlsLocked = roundLocked || round.hasPicks;
                                return (
                                <Box key={round.uiId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                    <Stack gap={1.5}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <Typography variant="subtitle1">Round {round.index || '-'}</Typography>
                                                <Chip
                                                    size="small"
                                                    label={roundStatus.toLowerCase()}
                                                    color={roundStatusColor(roundStatus)}
                                                />
                                            </Stack>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                {editingTemplate && round.id && roundStatus !== 'CANCELLED' && (
                                                    <Tooltip title="Cancel round and override all its picks as cancelled">
                                                        <IconButton color="warning" onClick={() => cancelRound(round)}>
                                                            <CancelOutlinedIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <IconButton
                                                    color="error"
                                                    disabled={roundLocked || round.hasPicks}
                                                    onClick={() => setForm(prev => ({
                                                        ...prev,
                                                        rounds: prev.rounds.filter(item => item.uiId !== round.uiId),
                                                    }))}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Stack>
                                        </Stack>
                                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                                            <TextField
                                                label="Index"
                                                value={round.index}
                                                disabled={structuralControlsLocked}
                                                onChange={e => patchRound(round.uiId, { index: e.target.value })}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Starts at"
                                                type="datetime-local"
                                                value={round.startsAt}
                                                disabled={structuralControlsLocked}
                                                onChange={e => patchRound(round.uiId, { startsAt: e.target.value })}
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                label="Ends at"
                                                type="datetime-local"
                                                value={round.endsAt}
                                                disabled={structuralControlsLocked}
                                                onChange={e => patchRound(round.uiId, { endsAt: e.target.value })}
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Stack>
                                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ px: 1.5, py: 1 }}
                                            >
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    startIcon={expandedRoundMatches[round.uiId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    onClick={() => setExpandedRoundMatches(prev => ({
                                                        ...prev,
                                                        [round.uiId]: !prev[round.uiId],
                                                    }))}
                                                >
                                                    Matches ({round.matches.length})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    disabled={structuralControlsLocked}
                                                    onClick={() => {
                                                        patchRound(round.uiId, { matches: [...round.matches, emptyMatch()] });
                                                        setExpandedRoundMatches(prev => ({ ...prev, [round.uiId]: true }));
                                                    }}
                                                >
                                                    Add match
                                                </Button>
                                            </Stack>
                                            <Collapse in={Boolean(expandedRoundMatches[round.uiId])} timeout="auto" unmountOnExit>
                                                <Stack gap={1.5} sx={{ px: 1.5, pb: 1.5 }}>
                                                    {round.matches.length === 0 && (
                                                        <Typography variant="body2" color="text.secondary">No matches</Typography>
                                                    )}
                                                    {round.matches.map(match => (
                                                        <Box key={match.uiId} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                                                            <Stack gap={1}>
                                                                <Stack direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                                                                    <TextField
                                                                        label="Match ID"
                                                                        value={match.matchId}
                                                                        disabled={structuralControlsLocked}
                                                                        onChange={e => patchMatch(round.uiId, match.uiId, { matchId: e.target.value })}
                                                                        sx={{ minWidth: 160 }}
                                                                    />
                                                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                                        <RoundMatchDetailsForId
                                                                            matchId={match.matchId}
                                                                            matchDetailsById={matchDetailsById}
                                                                        />
                                                                    </Box>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={match.settlementStatus === 'EXCLUDED'}
                                                                                disabled={roundLocked || (round.hasPicks && match.settlementStatus === 'EXCLUDED')}
                                                                                onChange={e => patchMatch(round.uiId, match.uiId, {
                                                                                    settlementStatus: e.target.checked ? 'EXCLUDED' : 'PENDING',
                                                                                })}
                                                                            />
                                                                        }
                                                                        label="Excluded"
                                                                    />
                                                                    <IconButton
                                                                        color="error"
                                                                        disabled={structuralControlsLocked}
                                                                        onClick={() => patchRound(round.uiId, {
                                                                            matches: round.matches.filter(item => item.uiId !== match.uiId),
                                                                        })}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </Stack>
                                                                <Box>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Template bet groups available for this match
                                                                    </Typography>
                                                                    <RoundMatchBetGroups
                                                                        matchId={match.matchId}
                                                                        templateGroupIds={form.allowedBetTypeGroupIds}
                                                                        groupsByMatchId={matchBetGroupsById}
                                                                    />
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Collapse>
                                        </Box>
                                    </Stack>
                                </Box>
                                );
                            })}
                        </Stack>

                        <Divider />
                        <Stack gap={1}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">Promo metadata</Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setForm(prev => ({
                                        ...prev,
                                        promoMetadataRows: [...prev.promoMetadataRows, { uiId: uiId(), key: '', value: '' }],
                                    }))}
                                >
                                    Add metadata
                                </Button>
                            </Stack>
                            {form.promoMetadataRows.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No metadata</Typography>
                            )}
                            {form.promoMetadataRows.map(row => (
                                <Stack key={row.uiId} direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                                    <TextField
                                        label="Key"
                                        value={row.key}
                                        onChange={e => setForm(prev => ({
                                            ...prev,
                                            promoMetadataRows: prev.promoMetadataRows.map(item => item.uiId === row.uiId ? { ...item, key: e.target.value } : item),
                                        }))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Value"
                                        value={row.value}
                                        onChange={e => setForm(prev => ({
                                            ...prev,
                                            promoMetadataRows: prev.promoMetadataRows.map(item => item.uiId === row.uiId ? { ...item, value: e.target.value } : item),
                                        }))}
                                        fullWidth
                                    />
                                    <IconButton
                                        color="error"
                                        onClick={() => setForm(prev => ({
                                            ...prev,
                                            promoMetadataRows: prev.promoMetadataRows.filter(item => item.uiId !== row.uiId),
                                        }))}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Stack>
                            ))}
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveTemplate}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
